import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Filter, FilterMode } from "@/shared/lib/filter/filter";
import { UUID } from "@bitory-gmbh/nidavellir";
import { Check, ListFilter } from "lucide-react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Spinner } from "@/shared/components/ui/spinner";
import { cn } from "@/shared/lib/utils";
import { useSamplesDeliveryListQuery } from "@/services/backend/samples/api/delivery-api";
import { Separator } from "@/shared/components/ui/separator";
import { formatDateTime } from "@/shared/lib/utilities/date";

export type DeliveryFilterProps = {
  filter?: Filter<UUID>;
  onFilterChange: (filter: Filter<UUID>) => void;
  /** Allows to limit the deliveries to filter from to a certain documents */
  document?: Filter<UUID>;
  /* isNullOption can be set with a label. If set a button with
   * the set label will be shown and allows user to filter by IsNull. */
  isNullOption?: string;
  hidden?: boolean;
};

export function DeliveryFilter({
  filter,
  onFilterChange,
  document,
  isNullOption,
  hidden,
}: DeliveryFilterProps) {
  // This allows us to accept undefined filters,
  // which is common in list requests. This makes
  // it nicer to call the component.
  const filterVal = filter ?? { active: false, values: [] };
  // simplifies some code below
  const currentValues = filterVal.values ?? [];

  const { data, isLoading, error } = useSamplesDeliveryListQuery({ document });

  if (hidden) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <ListFilter />
          {t("Lieferung")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command>
          <CommandInput placeholder={t("Name")} />
          {isLoading && (
            <span className="flex w-full items-center justify-center gap-2 py-3 text-sm">
              <Spinner />
              {t("Lade Daten ...")}
            </span>
          )}
          {error && (
            <RTKQueryErrorAlert error={error} className="border-none" />
          )}
          {data && (
            <CommandList>
              <CommandEmpty className="py-3 text-center text-sm">
                {data.items.length === 0
                  ? t("Bitte eine Lieferung anlegen")
                  : t("Name nicht gefunden")}
              </CommandEmpty>
              <CommandGroup hidden={data.items.length === 0}>
                {isNullOption && (
                  <div className="grid gap-1">
                    <CommandItem
                      value={isNullOption}
                      onSelect={() => {
                        onFilterChange({
                          active: !filterVal.active,
                          mode: FilterMode.IsNull,
                        });
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 size-4",
                          filterVal.active &&
                            filterVal.mode === FilterMode.IsNull
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      <span>{isNullOption}</span>
                    </CommandItem>
                    <Separator />
                  </div>
                )}
                {data.items.map((delivery) => (
                  <CommandItem
                    key={delivery.id}
                    value={delivery.trackingId}
                    onSelect={() => {
                      const values = currentValues.includes(delivery.id)
                        ? currentValues.filter((v) => v !== delivery.id)
                        : [...currentValues, delivery.id];
                      onFilterChange({ active: values.length > 0, values });
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        currentValues.includes(delivery.id)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {t(
                      `${formatDateTime(delivery.createdAt)} Tracking ID: ${delivery.trackingId}`,
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
